import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';

import Layout from '@/components/layout/LayoutWrapper';
import EnTermsEUMDX from '@/markdown/terms/tos-eu/en.mdx';
import EnTermsMDX from '@/markdown/terms/tos/en.mdx';
import { Locale } from '@/store/locale/types';
import { getIsEU } from '@/utils/admin';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

interface TermsProps {
	initialState: {
		isEU: boolean;
		locale: Locale;
	};
}

interface LocaleMdxProps {}

/*
 * Need to use switch statement due to need for static strings in `import` function within `dynamic()`
 * https://nextjs.org/docs/advanced-features/dynamic-import
 * This might eventually get fixed: https://github.com/vercel/next.js/discussions/33980
 */
const localeMdx = (isEU: boolean = false, locale: Locale = 'en') => {
	let defaultMDX = EnTermsMDX;

	if (isEU) {
		defaultMDX = EnTermsEUMDX;
		switch (locale) {
			case 'fr':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/fr.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'ja':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/ja.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'es':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/es.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'de':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/de.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'pt':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/pt.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'ko':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/ko.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'it':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/it.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'pl':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/pl.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			case 'zh':
				return dynamic<LocaleMdxProps>(() =>
					import('@/markdown/terms/tos-eu/zh.mdx').then(mdx => mdx).catch(() => defaultMDX),
				);
			// English fallback
			case 'en':
			default:
				return defaultMDX;
		}
	}

	switch (locale) {
		case 'fr':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/fr.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'ja':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/ja.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'es':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/es.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'de':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/de.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'pt':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/pt.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'ko':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/ko.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'it':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/it.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'pl':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/pl.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		case 'zh':
			return dynamic<LocaleMdxProps>(() =>
				import('@/markdown/terms/tos/zh.mdx').then(mdx => mdx).catch(() => defaultMDX),
			);
		// English fallback
		case 'en':
		default:
			return defaultMDX;
	}
};

const TermsPage = ({ initialState: { isEU, locale } }: TermsProps) => {
	const LocaleMdx = localeMdx(isEU, locale);
	// TODO: refactor this code to follow best practices
	/* eslint-disable react-hooks/static-components */
	return (
		<Layout pageName="terms-of-service" title="Calm - Terms of Service">
			<LocaleMdx />
		</Layout>
	);
	/* eslint-enable react-hooks/static-components */
};

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);
	const device = initialState?.device ?? null;
	const locale = initialState?.locale ?? 'en';

	return {
		props: {
			initialState: {
				isEU: getIsEU(device),
				locale,
			},
			hideAcceptCookiesModal: true,
		},
	};
};

export default TermsPage;
